import { useState } from 'react';
import { Icon, type IconName } from '@/components/ui/icon.js';
import { ORIGINALS } from './catalog.js';
import { originalCardIcon } from './cardIcon.js';
import { useOriginalsCatalog } from './useOriginalsCatalog.js';

interface Props {
  code: string;
  /** The badge's size in CSS pixels. */
  size: number;
  className?: string;
}

/** Share of the badge the card's hero object fills, leaving it a little air. */
const HERO_FILL = 0.88;

/**
 * A game's badge: its lobby card's hero object (or, without one, the top of
 * its Tile) on the site's sunken surface, so the menu and the info bar show
 * the same picture as the card. Its lucide icon when the catalog has no art for
 * it yet, or the picture fails to load. The badge keeps its size either way.
 */
export function OriginalIcon({ code, size, className }: Props) {
  const catalog = useOriginalsCatalog();
  const heroSize = Math.round(size * HERO_FILL);
  const art = originalCardIcon(catalog.get(code), size, new Date());
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const fallback: IconName = ORIGINALS.find((o) => o.code === code)?.icon ?? 'play';
  const showArt = art !== null && art.src !== failedSrc;

  return (
    <span
      aria-hidden
      className={[
        'relative grid shrink-0 place-items-center overflow-hidden rounded-xl bg-[var(--bg-sunken)] text-[var(--text-secondary)]',
        className ?? '',
      ].join(' ')}
      style={{ width: size, height: size }}
    >
      {showArt ? (
        <img
          src={art.src}
          srcSet={art.srcSet}
          alt=""
          draggable={false}
          onError={() => setFailedSrc(art.src)}
          className={
            art.kind === 'hero'
              ? 'object-contain drop-shadow-[0_2px_3px_rgba(0,0,0,.35)] select-none'
              : 'absolute inset-0 size-full object-cover object-[50%_30%] select-none'
          }
          style={art.kind === 'hero' ? { width: heroSize, height: heroSize } : undefined}
        />
      ) : (
        <Icon name={fallback} size={Math.round(size * 0.48)} />
      )}
    </span>
  );
}
